import React, { useEffect, useState } from "react";
import { createClosePack, getClosePacks, closePackUrl, emailClosePack, getSettings, updateSettings, getClosePackSchedule, runClosePackSchedule, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { FileDown, Loader2, BookCheck, Download, Mail, CheckCircle2, CalendarClock, Play, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

const thisMonth = () => new Date().toISOString().slice(0, 7);

const LedgerChips = ({ summary, packId }) => {
  const ledgers = summary.ledgers?.length ? summary.ledgers : summary.xero ? [summary.xero] : [];
  if (!ledgers.length) return null;
  const combined = summary.ledgers_combined;
  return (
    <>
      {ledgers.map((l, i) => (
        <span key={l.provider || i} className={l.gap_amount || l.issues ? "text-amber-500" : "text-emerald-500"} data-testid={`close-pack-${l.provider || "xero"}-${packId}`}>
          {" "}· {l.label || "Xero"} {l.synced}/{l.approved}{l.gap_amount ? ` gap ${money(l.gap_amount, summary.base_currency)}` : " reconciled"}
        </span>
      ))}
      {combined && <span className="text-muted-foreground" data-testid={`close-pack-combined-${packId}`}> · both {combined.synced}/{combined.approved}</span>}
    </>
  );
};

export const ClosePackDialog = () => {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(thisMonth());
  const [busy, setBusy] = useState(false);
  const [packs, setPacks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [mailing, setMailing] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [running, setRunning] = useState(false);

  const loadSchedule = () => getClosePackSchedule().then(setSchedule);
  useEffect(() => { if (open) { getClosePacks().then(setPacks); getSettings().then(setSettings); loadSchedule(); } }, [open]);

  const saveSchedule = async (patch) => {
    try {
      const saved = await updateSettings({ ...settings, ...patch });
      setSettings(saved);
      await loadSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await runClosePackSchedule(true);
      toast.success(`Generated ${r.pack.month} pack${r.pack.emailed_to?.length ? ` and emailed ${r.pack.emailed_to.length} recipient(s)` : ""}`);
      setPacks(await getClosePacks());
      await loadSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scheduled run failed");
    }
    setRunning(false);
  };

  const toggleAuto = async (v) => {
    const saved = await updateSettings({ ...settings, close_pack_auto_email: v });
    setSettings(saved);
    toast.success(v ? "New close packs will be emailed automatically" : "Auto-email off");
  };

  const mail = async (p) => {
    setMailing(p.id);
    try {
      const r = await emailClosePack(p.id, user);
      toast.success(`Emailed to ${r.sent.join(", ")}`);
      setPacks(await getClosePacks());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email close pack");
    }
    setMailing(null);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const p = await createClosePack(month, user);
      toast.success(`Close pack for ${month} ready — ${p.summary.invoices} invoices, ${p.summary.exceptions} open exception(s)${p.emailed_to?.length ? ` · emailed to ${p.emailed_to.length} recipient(s)` : ""}`);
      setPacks(await getClosePacks());
      window.open(closePackUrl(p.id), "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate close pack");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="close-pack-btn" variant="outline"><BookCheck className="h-4 w-4 mr-2" /> Close Pack</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="close-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookCheck className="h-4 w-4 text-primary" /> Month-end close pack</DialogTitle>
          <DialogDescription>One PDF: KPIs, spend by GL code, top suppliers, FX, every unresolved exception, and who approved what.</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Month</label>
            <Input data-testid="close-pack-month" type="month" value={month} max={thisMonth()} onChange={(e) => setMonth(e.target.value)} className="font-mono mt-1" />
          </div>
          <Button data-testid="close-pack-generate" onClick={generate} disabled={busy || !/^\d{4}-\d{2}$/.test(month)}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Generate PDF
          </Button>
        </div>
        {settings && (
          <label className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="close-pack-auto-email">
            <span className="text-muted-foreground">
              Email each new pack (PDF attached) to the alert recipients
              {settings.alert_emails?.length ? <span className="block font-mono text-[11px] text-foreground/80">{settings.alert_emails.join(", ")}</span>
                : <span className="block text-amber-500">No recipients configured — add them under Rules &amp; Accounts</span>}
            </span>
            <Switch data-testid="close-pack-auto-email-toggle" checked={!!settings.close_pack_auto_email} onCheckedChange={toggleAuto} />
          </label>
        )}
        {settings && schedule && (
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="close-pack-schedule">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 font-medium"><CalendarClock className="h-3.5 w-3.5 text-primary" /> Auto-generate previous month's pack</span>
              <Switch data-testid="close-pack-schedule-toggle" checked={!!settings.close_pack_schedule_enabled} onCheckedChange={(v) => saveSchedule({ close_pack_schedule_enabled: v })} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground">
              on day
              <Input data-testid="close-pack-schedule-day" type="number" min="1" max="28" className="h-7 w-16 font-mono text-xs" value={settings.close_pack_schedule_day ?? 1}
                     onChange={(e) => setSettings({ ...settings, close_pack_schedule_day: Number(e.target.value) })}
                     onBlur={(e) => saveSchedule({ close_pack_schedule_day: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })} />
              at
              <Input data-testid="close-pack-schedule-hour" type="number" min="0" max="23" className="h-7 w-16 font-mono text-xs" value={settings.close_pack_schedule_hour ?? 7}
                     onChange={(e) => setSettings({ ...settings, close_pack_schedule_hour: Number(e.target.value) })}
                     onBlur={(e) => saveSchedule({ close_pack_schedule_hour: Math.min(23, Math.max(0, Number(e.target.value) || 0)) })} />
              :00 UTC
              <Button data-testid="close-pack-schedule-run" size="sm" variant="ghost" className="h-7 ml-auto" onClick={runNow} disabled={running}>
                {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />} Run now
              </Button>
            </div>
            <div className="mt-1.5 text-muted-foreground" data-testid="close-pack-schedule-status">
              {schedule.enabled ? <>Next run: <span className="font-mono text-foreground/80">{new Date(schedule.next_run_at).toLocaleString()}</span></> : "Scheduler off"}
              {schedule.last_auto && <> · Last auto pack: <span className="font-mono text-foreground/80">{schedule.last_auto.month}</span> on {new Date(schedule.last_auto.created_at).toLocaleDateString()}{schedule.last_auto.emailed_to?.length ? ` · emailed ${schedule.last_auto.emailed_to.length}` : ""}</>}
            </div>
          </div>
        )}
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Previous packs</div>
          {packs.length === 0 ? (
            <p className="text-xs text-muted-foreground italic" data-testid="close-pack-empty">None yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border max-h-56 overflow-auto d20-scroll" data-testid="close-pack-list">
              {packs.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`close-pack-row-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono font-medium">{p.month} {p.source === "scheduled" && <span className="rounded bg-primary/15 px-1 py-0.5 text-[9px] font-semibold uppercase text-primary" data-testid={`close-pack-scheduled-${p.id}`}>auto</span>} <span className="text-muted-foreground font-normal">· {p.summary.invoices} inv · {money(p.summary.approved_value, p.summary.base_currency)} approved · {p.summary.exceptions} exceptions{p.summary.duplicates_stopped?.count > 0 && <span className="text-amber-500" data-testid={`close-pack-dups-${p.id}`}> · {p.summary.duplicates_stopped.count} duplicate{p.summary.duplicates_stopped.count === 1 ? "" : "s"} stopped ({money(p.summary.duplicates_stopped.value, p.summary.base_currency)})</span>}<LedgerChips summary={p.summary} packId={p.id} /></span></div>
                    <div className="text-muted-foreground">{new Date(p.created_at).toLocaleString()} · {p.created_by} · {(p.size / 1024).toFixed(0)} KB</div>
                    {p.emailed_at && <div className="flex items-center gap-1 text-emerald-500" data-testid={`close-pack-emailed-${p.id}`}><CheckCircle2 className="h-3 w-3" /> emailed {new Date(p.emailed_at).toLocaleString()} to {p.emailed_to?.length} recipient(s)</div>}
                    {p.email_error && <div className="text-red-500" data-testid={`close-pack-email-error-${p.id}`}>email failed: {p.email_error}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`close-pack-email-${p.id}`} onClick={() => mail(p)} disabled={mailing === p.id} title="Email to alert recipients" className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50">
                      {mailing === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} {p.emailed_at ? "Resend" : "Email"}
                    </button>
                    <a data-testid={`close-pack-open-${p.id}`} href={closePackUrl(p.id)} target="_blank" rel="noreferrer" title="Open PDF in a new tab" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open</a>
                    <a data-testid={`close-pack-download-${p.id}`} href={`${closePackUrl(p.id)}?download=1`} title="Download PDF" className="inline-flex items-center gap-1 text-primary hover:underline"><Download className="h-3.5 w-3.5" /></a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
